import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Landing from "@/pages/Landing";
import TradeLanding from "@/pages/TradeLanding";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import PublicBudget from "@/pages/PublicBudget";
import PublicInvoice from "@/pages/PublicInvoice";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Generator from "@/pages/Generator";
import Budgets from "@/pages/Budgets";
import BudgetDetail from "@/pages/BudgetDetail";
import BudgetEdit from "@/pages/BudgetEdit";
import Clients from "@/pages/Clients";
import Templates from "@/pages/Templates";
import Profile from "@/pages/Profile";
import Billing from "@/pages/Billing";
import Cashflow from "@/pages/Cashflow";
import Referrals from "@/pages/Referrals";
import Admin from "@/pages/Admin";
import Accounting from "@/pages/Accounting";
import Analytics from "@/pages/Analytics";
import Files from "@/pages/Files";
import Bc3Import from "@/pages/Bc3Import";
import Bc3Viewer from "@/pages/Bc3Viewer";
import GastoRapido from "@/pages/GastoRapido";
import Install from "@/pages/Install";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import CookieBanner from "@/components/CookieBanner";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiesPolicy from "@/pages/CookiesPolicy";
import LegalNotice from "@/pages/LegalNotice";

function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-slate-500">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  // Paywall: block users without active subscription. Admin & free_forever bypass via has_access.
  const ALLOWED_NO_ACCESS = ["/billing", "/profile"];
  const path = window.location.pathname;
  const isAllowedWhenNoAccess = ALLOWED_NO_ACCESS.some((p) => path.startsWith(p));
  if (user.has_access === false && user.role !== "admin" && !isAllowedWhenNoAccess) {
    return <Navigate to="/billing?paywall=1" replace />;
  }
  // Force onboarding if not completed (except admin, except no-access users — they pay first)
  if (
    !user.onboarding_completed &&
    user.role !== "admin" &&
    user.has_access !== false &&
    path !== "/onboarding"
  ) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function OnboardingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-slate-500">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Onboarding />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" richColors />
        <AnalyticsTracker />
        <CookieBanner />
        <Routes>
          <Route path="/politica-de-privacidad" element={<PrivacyPolicy />} />
          <Route path="/politica-de-cookies" element={<CookiesPolicy />} />
          <Route path="/aviso-legal" element={<LegalNotice />} />
          <Route path="/" element={<Landing />} />
          <Route path="/presupuestos" element={<TradeLanding slug="general" />} />
          <Route path="/presupuestos-reformas" element={<TradeLanding slug="reformas" />} />
          <Route path="/presupuestos-pintura" element={<TradeLanding slug="pintura" />} />
          <Route path="/presupuestos-electricidad" element={<TradeLanding slug="electricidad" />} />
          <Route path="/presupuestos-fontaneria" element={<TradeLanding slug="fontaneria" />} />
          <Route path="/presupuestos-carpinteria" element={<TradeLanding slug="carpinteria" />} />
          <Route path="/presupuestos-limpieza" element={<TradeLanding slug="limpieza" />} />
          <Route path="/presupuestos-jardineria" element={<TradeLanding slug="jardineria" />} />
          <Route path="/presupuestos-instalaciones" element={<TradeLanding slug="instalaciones" />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/instalar" element={<Install />} />
          <Route path="/p/:token" element={<PublicBudget />} />
          <Route path="/pi/:token" element={<PublicInvoice />} />
          <Route path="/onboarding" element={<OnboardingRoute />} />
          <Route element={<Protected><AppLayout /></Protected>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/generator" element={<Generator />} />
            <Route path="/budgets" element={<Budgets />} />
            <Route path="/budgets/:id" element={<BudgetDetail />} />
            <Route path="/budgets/:id/edit" element={<BudgetEdit />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/cashflow" element={<Cashflow />} />
            <Route path="/accounting" element={<Accounting />} />
            <Route path="/templates" element={<Templates />} />
            <Route path="/files" element={<Files />} />
            <Route path="/bc3-import" element={<Bc3Import />} />
            <Route path="/bc3-viewer/:fileId" element={<Bc3Viewer />} />
            <Route path="/gasto-rapido" element={<GastoRapido />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/referrals" element={<Referrals />} />
            <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
            <Route path="/admin/analytics" element={<Protected admin><Analytics /></Protected>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
